import MainButton from '@/components/ui/custom/buttons/MainButton';
import SaveCancelGroup from '@/components/ui/custom/buttons/SaveCancelGroup';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import AvatarName from '@/components/ui/custom/dataDisplay/AvatarName';
import Text from '@/components/ui/custom/dataDisplay/Text';
import ChoiceSelector from '@/components/ui/custom/dataInput/ChoiceSelector';
import InfiniteSearchInput from '@/components/ui/custom/dataInput/InfiniteSearchInput';
import TextInput from '@/components/ui/custom/dataInput/TextInput';
import MyCkeditor from '@/components/editor/MyCkeditor';
import ResourceFileUpload from '@/components/resource/form/ResourceFileUpload';
import TopicsContainer from '@/components/topics/TopicsContainer';
import { MAX_PROJECT_VIDEO_SIZE, REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useHackathon from '@/data/hook/hackathon/useHackathon';
import useAuth from '@/data/hook/useAuth';
import { getProjectData } from '@/data/services/projectServices';
import {
  flagsQueryKeys,
  toolsQueryKeys,
  unitsQueryKeys,
  usersQueryKeys,
} from '@/data/services/querykeys';
import { listTools } from '@/data/services/toolServices';
import { isStudent, isTeacher } from '@/functions/auth';
import { handleUserFullName } from '@/functions/handleUserFullName';
import { ImageFolderEnum } from '@/models/CkEditor';
import { ProjectCourseFlagEnum } from '@/models/Flag';
import { ApiError } from '@/models/Errors';
import Flag from '@/models/Flag';
import { Project } from '@/models/Project';
import User, { UserStatusEnum, UserTypeEnum } from '@/models/User';
import { Breakpoints } from '@/utils/Breakpoint';
import cleanupObj from '@/utils/cleanupObj';
import { getAuthorizedUnits } from '@/utils/getAuthorizedUnits';
import ProjectPermissions from '@/utils/permissions/ProjectPermission';
import { ArrowsRightLeftIcon } from '@heroicons/react/24/outline';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { isEmpty, isEqual } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { twMerge } from 'tailwind-merge';
import ProjectHackathonForm from '../hackathon/ProjectHackathonForm';
import ProjectImport from '../hackathon/ProjectImport';
import SingleFileUploader from '@/components/ui/custom/input/SingleFileUploader';
import ProjectEmbedPreview from '../view/ProjectEmbedPreview';
import alert from '@/utils/shared/alert';

export type ProjectFields = Project & {
  imageFile?: File;
  courseFlag?: Flag;
  isHackathon?: boolean;
};
type SubmitFn = (project: ProjectFields) => void;

type ProjectFormProps = {
  project?: Project;
  isLoading?: boolean;
  saveButtonText?: string;
  onSubmit: SubmitFn;
  hasChanges?: boolean;
  setHasChanges?: (value: boolean) => void;
};

export default function ProjectForm({
  project,
  onSubmit,
  saveButtonText,
  setHasChanges,
  hasChanges,
  isLoading,
}: ProjectFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'projects.fields',
  });

  const isMdScreen = useMediaQuery({
    minWidth: Breakpoints.md,
  });
  const [tCourseType] = useTranslation('translation', {
    keyPrefix: 'courseType',
  });
  const { hackathon } = useHackathon();

  const { user } = useAuth();

  const isStudentUser = isStudent(user?.userType);
  const isTeacherUser = isTeacher(user?.userType);

  const permission = new ProjectPermissions({
    user,
    model: project,
    hackathon,
  });

  const { data: unit, isInitialLoading: isLoadingUnit } = useQuery({
    ...unitsQueryKeys.get(user?.unitsIds.at(0) || 0),
    enabled:
      !!user?.unitsIds.length && !project?.units?.length && !isStudentUser,
  });

  const defaultValues = useMemo(
    () => ({
      ...project,
      teacherMentor:
        project?.teacherMentor || (isTeacherUser ? user : undefined),
      users: project?.users || (isStudentUser ? [user] : []),
      units:
        project?.units || (user?.unitsIds.length && unit ? [unit] : undefined),
      courseFlag: project?.flags?.filter(v =>
        Object.values<string>(ProjectCourseFlagEnum).includes(v.name),
      )?.[0],
      isHackathon: project?.flags?.some(flag => flag.id === hackathon?.flagId),
    }),
    [project, isTeacherUser, user, isStudentUser, unit, hackathon?.flagId],
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useForm<ProjectFields>({
    defaultValues,
  });
  const isHackathon = watch('isHackathon') ?? false;

  const { field: usersField } = useController({
    name: 'users',
    control,
    rules: {
      required: t('required'),
    },
  });
  const { field: teacherField } = useController({
    name: 'teacherMentor',
    control,
    rules: {
      required: isHackathon ? t('required') : false,
    },
  });
  const { field: toolField } = useController({
    name: 'tool',
    control,
    rules: {
      required: t('required'),
    },
  });
  const { field: unitField } = useController({
    name: 'units',
    control,
  });
  const { field: courseFlagField } = useController({
    name: 'courseFlag',
    control,
    rules: {
      required: isHackathon ? t('required') : false,
    },
  });
  const { field: descriptionField } = useController({
    name: 'description',
    control,
    rules: {
      required: t('required'),
    },
  });
  const { field: imageField } = useController({
    name: 'image',
    control,
    rules: {
      required: isHackathon ? t('required') : false,
    },
  });
  const { field: documentationField } = useController({
    name: 'documentation',
    control,
    rules: {
      required: isHackathon ? t('required') : false,
    },
  });
  const { field: videoField } = useController({
    name: 'video',
    control,
    rules: {
      required: false,
    },
  });

  const handleImport = async (projectUrl: string) => {
    const { results } = await listTools({ site: projectUrl });
    const tool = results.at(0);
    if (!tool) {
      throw new Error('Could not find the tool used in the project');
    }
    const fields = watch();
    const metadata = await getProjectData({
      tool: tool.name,
      url: projectUrl,
    });
    reset({ ...fields, ...metadata, projectUrl, tool });
    return metadata;
  };

  function handleErrorMessage(error: ApiError) {
    if (error.codes.includes('request_has_no_metadata')) {
      return alert.error(t('projectUrl.noMetadata'));
    }
    setError('projectUrl', { message: error.getErrorMessage() });
  }

  const {
    mutate: onImport,
    isPending: isImporting,
    error,
    data: importData,
  } = useMutation(handleImport, {
    onError: (error: AxiosError<ApiError>, projectUrl) => {
      const api = new ApiError(error);
      const isRequestError = error instanceof AxiosError;
      const shoulddisplayErrorMessage =
        api.statusCode !== 504 && isRequestError;

      if (shoulddisplayErrorMessage) {
        handleErrorMessage(api);
      }
      if (
        !shoulddisplayErrorMessage ||
        api.codes.includes('request_has_no_metadata')
      ) {
        setValue('projectUrl', projectUrl, {
          shouldValidate: !shoulddisplayErrorMessage,
        });
      }
    },
  });

  const projectUrl = watch('projectUrl');

  useEffect(() => {
    const subscription = watch(fields => {
      setHasChanges?.(!isEqual(cleanupObj(fields), cleanupObj(defaultValues)));
    });
    return () => subscription.unsubscribe();
  }, [watch, setHasChanges, defaultValues]);

  const onSubmitClean: SubmitFn = fields => onSubmit(fields);

  useEffect(() => {
    const isHackathon = project?.flags?.some(
      flag => flag.id === hackathon?.flagId,
    );

    if (isHackathon) {
      setValue('isHackathon', isHackathon);
    }
  }, [hackathon, project, setValue]);

  useEffect(() => {
    if (unit && !project?.units?.length) setValue('units', [unit]);
  }, [unit, setValue, project]);

  const disableUnitInput = (getAuthorizedUnits(user)?.length || 0) === 1;

  return (
    <form
      className="gap-3.5 w-full relative md:min-h-[calc(100vh-140px)] md:flex-row md:flex"
      onSubmit={handleSubmit(onSubmitClean)}
    >
      <ConditionalRenderer condition={!isMdScreen}>
        <div className="flex gap-2.5 flex-wrap mb-2.5">
          <ProjectImport
            onImport={onImport}
            control={control}
            isLoading={isImporting}
            projectUrl={projectUrl}
          />
          <ConditionalRenderer condition={permission.hasTeacherPrivileges()}>
            <ProjectHackathonForm
              description={watch('description')}
              isHackathon={isHackathon}
              documentation={watch('documentation')}
              video={watch('video')}
              setIsHackathon={value => setValue('isHackathon', value)}
            />
          </ConditionalRenderer>
        </div>
      </ConditionalRenderer>
      <TopicsContainer className="md:w-60 xl:w-96 lg:w-72 shrink-0 w-full flex flex-col gap-3 md:h-[calc(100vh-140px)]">
        <div className="flex flex-col gap-1">
          <ImageUploader
            value={imageField.value}
            onChange={(url, file) => {
              imageField.onChange(url);
              setValue('imageFile', file);
            }}
            accept=".png,.jpg,.jpeg"
            label={t('banner.placeholder')}
            swapText={t('banner.swapText')}
          />
          <Text text={errors.image?.message} className="text-error text-14" />
        </div>
        <ConditionalRenderer condition={isHackathon}>
          <TextInput
            label={t('equipName.label')}
            placeholder={t('equipName.placeholder')}
            labelPosition="top"
            errorLabelText={errors.equipName?.message}
            className={{
              label: 'text-14 mb-1.5',
              input: 'w-full',
            }}
            {...register('equipName', {
              maxLength: {
                value: 50,
                message: t('equipName.maxLength', { max: 50 }),
              },
            })}
          />
        </ConditionalRenderer>
        <ConditionalRenderer condition={permission.hasTeacherPrivileges()}>
          <ChoiceSelector
            service={usersQueryKeys.list}
            getKey={user => user.id}
            displayName={handleUserFullName}
            filters={{
              userType: [UserTypeEnum.STUDENT],
              unitId: getAuthorizedUnits(user),
            }}
            render={ChoiceSelectRender}
            options={{ staleTime: REQUEST_STALE_TIME_IN_MS }}
            defaultValue={usersField.value}
            input={{
              label: t('users.label'),
              placeholder: t('users.placeholder'),
              className: {
                label: 'text-14 mb-1.5',
                input: 'w-full',
              },
              errorLabelText: errors.users?.message,
            }}
            onChange={usersField.onChange}
          />
        </ConditionalRenderer>
        <ConditionalRenderer condition={permission.hasUnitPrivileges()}>
          <ConditionalRenderer condition={isHackathon}>
            <InfiniteSearchInput
              displayName={teacher => handleUserFullName(teacher)}
              service={usersQueryKeys.list}
              filters={{
                unitId: getAuthorizedUnits(user),
                userType: [UserTypeEnum.TEACHER],
                status: [UserStatusEnum.REGISTRED],
              }}
              selectedItem={teacherField.value}
              onSelect={teacherField.onChange}
              disabled={isStudentUser}
              input={{
                label: t('teacherMentor.label'),
                testId: 'userInput',
                className: {
                  label: 'text-14 mb-1.5',
                  input: 'w-full',
                },
                placeholder: t('teacherMentor.placeholder'),
                disabled: isStudentUser,
                errorLabelText: errors.teacherMentor?.message,
              }}
              className={{ base: 'max-w-lg' }}
              options={{ staleTime: REQUEST_STALE_TIME_IN_MS }}
            />
          </ConditionalRenderer>
        </ConditionalRenderer>
        <InfiniteSearchInput
          displayName={tool => tool.name}
          service={toolsQueryKeys.list}
          selectedItem={toolField.value}
          onSelect={toolField.onChange}
          onDeselect={() => toolField.onChange(undefined)}
          input={{
            label: t('tool.label'),
            testId: 'toolSelect',
            className: {
              label: 'text-14 mb-1.5',
              input: 'w-full',
            },
            errorLabelText: errors.tool?.message,
            placeholder: t('tool.placeholder'),
          }}
          className={{ input: 'max-w-lg' }}
          options={{ staleTime: REQUEST_STALE_TIME_IN_MS }}
        />
        <InfiniteSearchInput
          displayName={tool => tCourseType(tool.name)}
          service={flagsQueryKeys.list}
          selectedItem={courseFlagField.value}
          onSelect={courseFlagField.onChange}
          onDeselect={() => courseFlagField.onChange(undefined)}
          filters={{
            name: Object.values(ProjectCourseFlagEnum).join(),
          }}
          input={{
            label: t('course.label'),
            className: { label: 'text-14 mb-1.5', input: 'w-full' },
            errorLabelText: errors.courseFlag?.message,
            placeholder: t('course.placeholder'),
          }}
          className={{ input: 'max-w-lg' }}
          options={{ staleTime: REQUEST_STALE_TIME_IN_MS }}
        />
        <ConditionalRenderer condition={permission.hasTeacherPrivileges()}>
          <InfiniteSearchInput
            service={unitsQueryKeys.list}
            displayName={unit => unit.name}
            selectedItem={unitField.value?.at(0)}
            onSelect={unit => {
              const units = [unit];
              unitField.onChange(units);
            }}
            filters={{
              idIn: getAuthorizedUnits(user)?.toString(),
              isActive: true,
            }}
            input={{
              label: t('units.label'),
              errorLabelText: errors?.units?.message,
              testId: 'selectUnit',
              isLoading: isLoadingUnit,
              disabled: disableUnitInput,
              placeholder: t('units.placeholder'),
              className: {
                label: 'text-14 mb-1.5',
                input: 'w-full',
                div: disableUnitInput
                  ? '!border-neutral-content border rounded-lg'
                  : '',
              },
            }}
          />
        </ConditionalRenderer>
      </TopicsContainer>

      <TopicsContainer className="flex flex-col grow gap-3 border-none h-full bg-transparent py-0">
        <ConditionalRenderer condition={isMdScreen}>
          <div className="flex gap-2.5 flex-wrap lg:flex-nowrap">
            <ProjectImport
              onImport={onImport}
              control={control}
              isLoading={isImporting}
              projectUrl={projectUrl}
            />
            <ConditionalRenderer condition={permission.hasTeacherPrivileges()}>
              <ProjectHackathonForm
                description={watch('description')}
                documentation={watch('documentation')}
                isHackathon={isHackathon}
                video={watch('video')}
                setIsHackathon={value => setValue('isHackathon', value)}
              />
            </ConditionalRenderer>
          </div>
        </ConditionalRenderer>

        <ConditionalRenderer condition={!!projectUrl}>
          <ProjectEmbedPreview
            projectMetadata={{ value: importData, error }}
            className={{
              base: 'h-80 xl:h-96 aspect-video bg-primary-content flex justify-center rounded-lg items-center p-4 max-w-full',
              iframe: 'aspect-video h-full w-full rounded-md',
            }}
            src={projectUrl || ''}
          />
        </ConditionalRenderer>
        <TextInput
          label={t('title.label')}
          placeholder={t('title.placeholder')}
          className={{ base: 'max-w-96', label: 'mb-1.5' }}
          errorLabelText={errors.title?.message}
          {...register('title', {
            required: t('required'),
          })}
        />
        <div className="flex gap-2.5 justify-start">
          <ConditionalRenderer condition={isHackathon}>
            <div className="relative w-full flex-col flex gap-1.5">
              <Text text={t('documentation.label')} className="text-14" />
              <SingleFileUploader
                value={documentationField.value}
                testId="fileUpload"
                onChange={documentationField.onChange}
                fileName={t('documentation.filename')}
                label={t('documentation.placeholder')}
                accept=".pdf"
              />
              <Text
                text={errors.documentation?.message}
                className="text-error text-14"
              />
            </div>
          </ConditionalRenderer>
          <ConditionalRenderer condition={isHackathon}>
            <div className="relative w-full flex-col flex gap-1.5">
              <Text text={t('video.label')} className="text-14" />
              <SingleFileUploader
                value={videoField.value}
                testId="videoUpload"
                onChange={videoField.onChange}
                fileName={t('video.filename')}
                label={t('video.placeholder')}
                accept=".mp4,.mov,.avi,.wmv,.flv,.mpeg,.mpg,.m4v,.webm"
                limit={MAX_PROJECT_VIDEO_SIZE}
              />
              <Text
                text={errors.video?.message}
                className="text-error text-14"
              />
            </div>
          </ConditionalRenderer>
        </div>
        <div className="relative w-full flex-col flex gap-1.5">
          <Text text={t('description.label')} className="text-14" />
          <MyCkeditor
            content={descriptionField.value || ''}
            folder={ImageFolderEnum.PROJECT}
            handleContentChange={descriptionField.onChange}
            placeholder={t('description.placeholder')}
          />
          <Text
            text={errors.description?.message}
            className="text-error text-14"
          />
        </div>
        <SaveCancelGroup
          className="justify-end"
          loading={isLoading}
          save={{
            text: saveButtonText,
            type: 'submit',
            disabled: !hasChanges || !isEmpty(errors),
          }}
          cancel={{
            onClick: () => {
              reset(defaultValues);
              setHasChanges?.(false);
            },
            disabled: !hasChanges,
          }}
        />
      </TopicsContainer>
    </form>
  );
}

type ImageUploaderProps = {
  value: string;
  label?: string;
  accept?: string;
  swapText?: string;
  onChange: (value: string, file: File) => void;
};

const ImageUploader = ({
  value,
  onChange,
  label,
  swapText,
  accept,
}: ImageUploaderProps) => {
  const onUploadImage = (file: File | null) => {
    if (!file) return;
    const fileUrl = URL.createObjectURL(file);
    onChange(fileUrl, file);
  };
  return (
    <div className="relative w-full h-44">
      <ResourceFileUpload
        onChange={e => onUploadImage(e)}
        label={label}
        value={value}
        accept={accept}
        className={{
          base: twMerge(
            'w-full h-40',
            value && 'absolute -bottom-1 h-6 opacity-0',
          ),
        }}
      />
      <ConditionalRenderer condition={value}>
        <div className="min-w-full flex flex-col items-center gap-1">
          <img src={value} className="h-40 w-full object-contain" alt={label} />
          <MainButton
            text={swapText || ''}
            color="custom"
            className="text-primary gap-1"
            icon={<ArrowsRightLeftIcon />}
          />
        </div>
      </ConditionalRenderer>
    </div>
  );
};

type ChoiceSelectRenderProps = {
  model: User;
};

const ChoiceSelectRender = ({ model }: ChoiceSelectRenderProps) => {
  return <AvatarName user={model} className="gap-1.5" />;
};
